import { useState } from 'react';
import { Alert, AlertDescription, AlertHeader } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { SelectField } from '@clay-run/glaze/select-field';

/**
 * U5 (plan `2026-09-24-005`, Item A) — the inline "Pick which play first"
 * prompt `ContactFlowTable` renders above the table when a rep asks for a
 * Signal-column sort or group-by the server would reject (the request is not
 * narrowed to one play — `requestNarrowsSignals`). Nothing goes on the wire
 * while this is open; Apply hands the chosen play NAME back to the table,
 * which applies the narrowing filter AND the pending sort/group together.
 *
 * Presentational only: the pending intent lives in `ContactFlowTable`
 * (plain `useState`, no refs), so the picked value here is local draft
 * state that dies with the prompt — every open starts on "no play chosen".
 *
 * Widget choice (plan "Deferred to Implementation — A prompt widget"): a
 * Glaze `SelectField`. It renders the whole option list (headless listbox
 * with first-letter typeahead), which is adequate for the live event-play
 * list; a searchable `GroupByPicker`-style list is the documented fallback
 * if the list outgrows it.
 */
export function SignalScopePrompt({
  kind,
  options,
  blockedReason,
  onApply,
  onCancel,
}: {
  /** Which Signal action is waiting — drives the "then this column will <sort|group>" copy. */
  kind: 'sort' | 'group';
  /** Live play NAMES (`useSignalSubtypeOptions`) — the value applied is the name itself (A4). */
  options: string[];
  /**
   * U14 (finding 5-1) — set when no play pick can scope the rep's current
   * filters (e.g. they are combined with OR). Rendered as-is, and Apply is
   * disabled: nothing may go on the wire the server would reject.
   */
  blockedReason?: string;
  onApply: (name: string) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const items = options.map(name => ({ value: name, label: name }));

  return (
    <div data-testid="signal-scope-prompt">
      <Alert status="info">
        <AlertHeader>Pick which play first</AlertHeader>
        <AlertDescription>
          {`Signal columns sort/group one play at a time. Choose a play to filter to, then this column will ${kind}.`}
        </AlertDescription>
        {blockedReason && <AlertDescription>{blockedReason}</AlertDescription>}
        <div className="mt-2 flex items-end gap-2">
          <div className="min-w-[240px]">
            <SelectField<string | null>
              label="Play"
              aria-label="Play"
              items={items}
              value={picked}
              onValueChange={(value: string | null) => setPicked(value)}
              placeholder={options.length === 0 ? 'Loading plays…' : 'Select a play…'}
              disabled={options.length === 0}
              size="sm"
            />
          </div>
          <Button type="button" size="sm" variant="primary" disabled={picked == null || !!blockedReason} onClick={() => { if (picked != null && !blockedReason) onApply(picked); }}>
            Apply
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Alert>
    </div>
  );
}
